import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import {
    BodyMediumRegular,
    ControlledTextInput,
    Form,
    FruitfulCustomIcon,
    H5Emphasized,
    isWeb,
    StyledH5Input,
    View,
    YStack,
} from 'smoothie';
import { z } from 'zod';

import type { UpdateBankAccountInfo } from '../../../../types/BankAccount';
import CreateEditButton from './CreateEditButton';

const PLACEHOLDER_TEXT = [
    'Emergency fund',
    'Walkin around money',
    'Wedding reception',
    'House down payment',
    'Llama farm',
    'New car',
    'Home renovation',
    'Creed tickets',
];

const spaceNameSchema = z.object({
    nickname: z
        .string()
        .min(2, 'Please enter a nickname')
        .max(30, 'Maximum 30 characters'),
});

type SpaceNameSchema = z.infer<typeof spaceNameSchema>;

interface NameProps {
    onPressNext: (updates?: Partial<UpdateBankAccountInfo>) => void;
    accountInfo: UpdateBankAccountInfo;
}

const Name = ({ onPressNext, accountInfo }: NameProps) => {
    const [placeholderIndex, setPlaceholderIndex] = useState(0);

    useEffect(() => {
        // Timer to cycle through placeholder text for account nickname every few seconds
        const timer = setInterval(() => {
            setPlaceholderIndex(
                (prevIndex) => (prevIndex + 1) % PLACEHOLDER_TEXT.length,
            );
        }, 1250);

        // Clear the interval timer when the component unmounts
        return () => {
            clearInterval(timer);
        };
    }, []);

    const {
        register,
        watch,
        control,
        handleSubmit,
        formState: { errors, isValid },
    } = useForm<SpaceNameSchema>({
        resolver: zodResolver(spaceNameSchema),
        defaultValues: {
            nickname: accountInfo?.nickname ?? '',
        },
    });

    const nickname = watch('nickname');

    const onSubmit: SubmitHandler<SpaceNameSchema> = ({ nickname }) => {
        onPressNext({ nickname });
    };

    return (
        <YStack flex={1} height="100%" justifyContent="space-between">
            <View marginBottom={32}>
                <H5Emphasized textAlign="center" marginVertical={12}>
                    Name your savings account
                </H5Emphasized>
                <BodyMediumRegular
                    textAlign="center"
                    color="$gray1100"
                    marginBottom={36}
                >
                    Personalize your savings space based on its purpose
                </BodyMediumRegular>
                <Form width="100%" onSubmit={handleSubmit(onSubmit)}>
                    <View alignSelf="center" marginBottom={36}>
                        <FruitfulCustomIcon
                            icon={accountInfo?.icon}
                            color={accountInfo?.color}
                            size="xl"
                            showShadow
                        />
                    </View>
                    <ControlledTextInput
                        Input={StyledH5Input}
                        name="nickname"
                        control={control}
                        register={register}
                        errors={errors}
                        value={nickname}
                        placeholder={PLACEHOLDER_TEXT[placeholderIndex]}
                        textAlign="center"
                        borderWidth={isWeb ? 1 : 0}
                        onSubmitEditing={() => {
                            void handleSubmit(onSubmit)();
                        }}
                    />
                </Form>
            </View>
            <CreateEditButton
                disabled={!isValid}
                onPress={handleSubmit(onSubmit)}
            />
        </YStack>
    );
};

export default Name;
